import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, ExternalLink, Plus, Send, MessageCircle,
  Globe, MapPin, Mail, Clock, Zap, ShieldCheck, Pencil, X, Check,
  Save
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/services/api";
import NoteThread from "@/components/notes/NoteThread";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";


const STATUS_COLORS = {
  "New": "bg-blue-100 text-blue-800 border-blue-200",
  "Contacted": "bg-purple-100 text-purple-800 border-purple-200",
  "Follow-up": "bg-yellow-100 text-yellow-800 border-yellow-200",
  "Call Booked": "bg-cyan-100 text-cyan-800 border-cyan-200",
  "Proposal Sent": "bg-orange-100 text-orange-800 border-orange-200",
  "Negotiating": "bg-amber-100 text-amber-800 border-amber-200",
  "Won": "bg-emerald-100 text-emerald-800 border-emerald-200",
  "Retainer": "bg-teal-100 text-teal-800 border-teal-200",
  "Lost": "bg-gray-100 text-gray-800 border-gray-200",
};

const ICP_COLORS = {
  "ICP-1 International": "bg-indigo-100 text-indigo-800 border-indigo-200",
  "ICP-2 India SMB": "bg-rose-100 text-rose-800 border-rose-200",
};

const OUTREACH_STATUS_COLORS = {
  "Drafted": "bg-gray-100 text-gray-800 border-gray-200",
  "Sent": "bg-blue-100 text-blue-800 border-blue-200",
  "Replied": "bg-green-100 text-green-800 border-green-200",
  "No Response": "bg-orange-100 text-orange-800 border-orange-200"
};

const TIER_COLORS = {
  "TIER 1": "bg-amber-100 text-amber-800 border-amber-300",
  "TIER 2": "bg-sky-100 text-sky-800 border-sky-300",
  "TIER 3": "bg-slate-100 text-slate-700 border-slate-300",
};

const CONFIDENCE_COLORS = {
  "High": "text-green-700 bg-green-50",
  "Medium": "text-yellow-700 bg-yellow-50",
  "Low": "text-red-700 bg-red-50",
};

// ─── Read-only info row ───────────────────────────────────────────────────────
const InfoRow = ({ label, value, children }) => {
  if (!value && !children) return null;
  return (
    <div>
      <p className="text-sm text-gray-500">{label}</p>
      {children ? <div className="mt-1">{children}</div> : <p className="text-base text-gray-900 mt-1">{value}</p>}
    </div>
  );
};

// ─── Editable field: text input ───────────────────────────────────────────────
const EditableField = ({ label, value, onChange, placeholder = "", type = "text" }) => (
  <div>
    <Label className="text-sm text-gray-500 font-normal mb-1 block">{label}</Label>
    <Input
      type={type}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder || label}
      className="border-amber-300 bg-amber-50/40 focus:bg-white h-9 text-sm"
    />
  </div>
);

// ─── Editable field: textarea ─────────────────────────────────────────────────
const EditableTextarea = ({ label, value, onChange, placeholder = "", rows = 4 }) => (
  <div>
    {label && <Label className="text-sm text-gray-500 font-normal mb-1 block">{label}</Label>}
    <Textarea
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="border-amber-300 bg-amber-50/40 focus:bg-white text-sm"
      rows={rows}
    />
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
const LeadDetail = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [lead, setLead] = useState(null);
  const [outreach, setOutreach] = useState([]);
  const [threadNotes, setThreadNotes] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [pipelineFields, setPipelineFields] = useState({ country: '', icp_type: '', assigned_to: '', budget: '', proposal_value: '', retainer_monthly: '', call_date: '', expected_close_date: '', invoice_amount: '', invoice_due_date: '', invoice_raised_date: '', invoice_paid_date: '', invoice_status: '' });
  const [newOutreach, setNewOutreach] = useState({ content: "", type: "LinkedIn", status: "Drafted" });
  const [dialogOpen, setDialogOpen] = useState(false);

  // ── Edit mode state ──────────────────────────────────────────────────────
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  const getAuthHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

  const fetchLead = async () => {
    try {
      const response = await api.get(`/api/leads/${id}`);
      const d = response.data;
      setLead(d);
      setPipelineFields({
        country: d.country || '', icp_type: d.icp_type || '',
        assigned_to: d.assigned_to || '', budget: d.budget || '',
        proposal_value: d.proposal_value || '', retainer_monthly: d.retainer_monthly || '',
        call_date: d.call_date || '', expected_close_date: d.expected_close_date || '',
        invoice_amount: d.invoice_amount || '', invoice_due_date: d.invoice_due_date || '',
        invoice_raised_date: d.invoice_raised_date || '', invoice_paid_date: d.invoice_paid_date || '',
        invoice_status: d.invoice_status || '',
      });
    } catch (error) {
      if (error.response?.status === 401) onLogout();
      else toast.error("Failed to fetch lead details");
    }
  };

  const fetchOutreach = async () => {
    try {
      const response = await api.get(`/api/leads/${id}/outreach`);
      setOutreach(response.data);
    } catch (error) {
      console.error("Error fetching outreach:", error);
    }
  };

  useEffect(() => { fetchLead(); fetchOutreach(); }, [id]);

  // ── Enter / exit edit mode ───────────────────────────────────────────────
  const enterEditMode = () => {
    setEditData({ ...lead });
    setEditMode(true);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditData({});
  };

  // Generic setter for flat fields
  const setField = (key) => (val) => setEditData(prev => ({ ...prev, [key]: val }));

  // Setters for nested trigger_signal / health_check objects
  const setTriggerField = (key) => (val) =>
    setEditData(prev => ({ ...prev, trigger_signal: { ...(prev.trigger_signal || {}), [key]: val } }));

  const setHealthField = (key) => (val) =>
    setEditData(prev => ({ ...prev, health_check: { ...(prev.health_check || {}), [key]: val } }));

  const saveEdit = async () => {
    setSaving(true);
    try {
      // Strip immutable/internal fields before sending
      const { _id, id: _id2, created_by, created_date, deleted, lead_format, ...payload } = editData;
      await axios.put(`${API}/leads/${id}`, payload, { headers: getAuthHeaders() });
      toast.success("Lead updated successfully");
      setEditMode(false);
      setEditData({});
      fetchLead();
    } catch (error) {
      if (error.response?.status === 401) onLogout();
      else toast.error("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (newStatus) => {
    try {
      await api.put(`/api/leads/${id}`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      fetchLead();
    } catch {
      toast.error("Failed to update status");
    }
  };

  const savePipelineFields = async () => {
    const payload = { ...pipelineFields };
    // Strip empty invoice_status (enum rejects empty string)
    if (!payload.invoice_status) delete payload.invoice_status;
    // Convert invoice_amount to number, or remove if empty
    if (payload.invoice_amount !== '' && payload.invoice_amount != null) {
      const num = parseFloat(payload.invoice_amount);
      payload.invoice_amount = isNaN(num) ? undefined : num;
    } else {
      delete payload.invoice_amount;
    }
    try {
      await api.put(`/api/leads/${id}`, payload);
      toast.success("Pipeline info saved");
      fetchLead();
    } catch {
      toast.error("Failed to save pipeline info");
    }
  };

  const createOutreach = async () => {
    try {
      await api.post(`/api/outreach`, { lead_id: id, ...newOutreach });
      toast.success("Outreach created");
      setNewOutreach({ content: "", type: "LinkedIn", status: "Drafted" });
      setDialogOpen(false);
      fetchOutreach();
    } catch {
      toast.error("Failed to create outreach");
    }
  };

  const updateOutreachStatus = async (outreachId, newStatus) => {
    try {
      await api.put(`/api/outreach/${outreachId}`, { status: newStatus });
      toast.success(`Marked as ${newStatus}`);
      fetchOutreach();
    } catch {
      toast.error("Failed to update outreach");
    }
  };

  if (!lead) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Loading...</p>
      </div>
    );
  }

  const isSupreme = lead.lead_format === 'supreme';
  const tier = lead.tags?.find(t => t.startsWith('TIER'));
  // In edit mode render from editData, otherwise from lead
  const d = editMode ? editData : lead;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <header className={`bg-white border-b transition-colors ${editMode ? 'border-amber-300' : 'border-gray-200'}`}>
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-button"
              variant="ghost"
              size="icon"
              onClick={() => { if (editMode) cancelEdit(); else navigate("/"); }}
              className="hover:bg-gray-100"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {editMode ? (
                  <Input
                    value={editData.company_name || ""}
                    onChange={(e) => setField("company_name")(e.target.value)}
                    className="text-xl font-semibold border-amber-300 bg-amber-50/40 h-9 w-64"
                  />
                ) : (
                  <h1 className="text-2xl font-semibold text-gray-900">{lead.company_name}</h1>
                )}
                {tier && (
                  <Badge className={`${TIER_COLORS[tier] || 'bg-gray-100 text-gray-700 border-gray-300'} border text-xs font-semibold px-2 py-0.5`}>
                    ★ {tier}
                  </Badge>
                )}
                {isSupreme && (
                  <Badge className="bg-violet-100 text-violet-800 border border-violet-300 text-xs px-2 py-0.5">
                    SUPREME
                  </Badge>
                )}
                {editMode && (
                  <Badge className="bg-amber-100 text-amber-800 border border-amber-300 text-xs px-2 py-0.5 animate-pulse">
                    Editing
                  </Badge>
                )}
              </div>

              <div className="flex items-center gap-3 mt-1 flex-wrap">
                {editMode ? (
                  <Input
                    value={editData.industry || ""}
                    onChange={(e) => setField("industry")(e.target.value)}
                    placeholder="Industry"
                    className="border-amber-300 bg-amber-50/40 h-7 text-sm w-48"
                  />
                ) : (
                  <>
                    <p className="text-sm text-gray-500">{lead.industry}</p>
                    {lead.location && (
                      <span className="flex items-center gap-1 text-sm text-gray-500">
                        <MapPin className="w-3 h-3" />{lead.location}
                      </span>
                    )}
                    {lead.website && (
                      <a
                        href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`}
                        target="_blank" rel="noopener noreferrer"
                        className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                      >
                        <Globe className="w-3 h-3" />{lead.website}
                      </a>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Edit / Save / Cancel buttons */}
            <div className="flex items-center gap-2 shrink-0">
              {editMode ? (
                <>
                  <Button
                    onClick={saveEdit}
                    disabled={saving}
                    className="bg-green-600 hover:bg-green-700 text-white gap-1"
                  >
                    <Check className="w-4 h-4" />
                    {saving ? "Saving…" : "Save Changes"}
                  </Button>
                  <Button variant="outline" onClick={cancelEdit} className="border-gray-300 gap-1">
                    <X className="w-4 h-4" />
                    Cancel
                  </Button>
                </>
              ) : (
                <Button variant="outline" onClick={enterEditMode} className="border-gray-300 gap-1">
                  <Pencil className="w-4 h-4" />
                  Edit Lead
                </Button>
              )}
              <Badge className={`${STATUS_COLORS[lead.status]} border text-sm px-3 py-1`}>
                {lead.status}
              </Badge>
            </div>
          </div>
        </div>
      </header>

      {/* ── Edit mode banner ─────────────────────────────────────────────────── */}
      {editMode && (
        <div className="bg-amber-50 border-b border-amber-200 px-6 py-2 text-center text-sm text-amber-800">
          ✏️ Edit mode — all fields are editable. Click <strong>Save Changes</strong> to apply or <strong>Cancel</strong> to discard.
        </div>
      )}

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* ── Main Content ─────────────────────────────────────────────────── */}
          <div className="lg:col-span-2 space-y-6">

            {/* Contact Info */}
            <Card className={`p-6 bg-white border ${editMode ? 'border-amber-200' : 'border-gray-200'}`}>
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>
              {editMode ? (
                <div className="grid grid-cols-2 gap-4">
                  <EditableField label="Contact Name"   value={editData.contact_name}   onChange={setField("contact_name")} />
                  <EditableField label="Designation"    value={editData.designation}    onChange={setField("designation")} />
                  <EditableField label="Company Size"   value={editData.company_size}   onChange={setField("company_size")} />
                  <EditableField label="Funding Amount" value={editData.funding_amount} onChange={setField("funding_amount")} />
                  <EditableField label="LinkedIn URL"   value={editData.linkedin_url}   onChange={setField("linkedin_url")} />
                  <EditableField label="Source URL"     value={editData.source_url}     onChange={setField("source_url")} />
                  {isSupreme && (
                    <>
                      <EditableField label="Email"        value={editData.email}        onChange={setField("email")} type="email" />
                      <EditableField label="Last Active"  value={editData.last_active}  onChange={setField("last_active")} />
                      <EditableField label="Investors"    value={editData.investors}    onChange={setField("investors")} />
                      <EditableField label="Funding Date" value={editData.funding_date} onChange={setField("funding_date")} />
                      <EditableField label="Website"      value={editData.website}      onChange={setField("website")} />
                      <EditableField label="Location"     value={editData.location}     onChange={setField("location")} />
                    </>
                  )}
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <InfoRow label="Contact Name" value={d.contact_name} />
                    <InfoRow label="Designation"  value={d.designation} />
                    <InfoRow label="Company Size" value={d.company_size} />
                    <InfoRow label="Funding"      value={d.funding_amount || "N/A"} />
                    {isSupreme && d.email && (
                      <InfoRow label="Email">
                        <a href={`mailto:${d.email}`} className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800">
                          <Mail className="w-4 h-4" />{d.email}
                        </a>
                      </InfoRow>
                    )}
                    {isSupreme && d.last_active && (
                      <InfoRow label="Last Active">
                        <span className="inline-flex items-center gap-1 text-sm text-gray-700">
                          <Clock className="w-4 h-4 text-gray-400" />{d.last_active}
                        </span>
                      </InfoRow>
                    )}
                    {isSupreme && d.investors    && <InfoRow label="Investors"    value={d.investors} />}
                    {isSupreme && d.funding_date && <InfoRow label="Funding Date" value={d.funding_date} />}
                  </div>
                  <div className="mt-4 flex flex-wrap gap-4">
                    {d.linkedin_url && (
                      <a href={d.linkedin_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800">
                        <ExternalLink className="w-4 h-4 mr-1" />LinkedIn Profile
                      </a>
                    )}
                    {d.source_url && (
                      <a href={d.source_url} target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center text-sm text-blue-600 hover:text-blue-800">
                        <ExternalLink className="w-4 h-4 mr-1" />Funding Source
                      </a>
                    )}
                  </div>
                </>
              )}
            </Card>

            {/* Pipeline Info */}
            <Card className="p-6 bg-white border border-indigo-100">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Sales Pipeline Info</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-gray-500">ICP Type</Label>
                  <Select value={pipelineFields.icp_type || "_none"} onValueChange={(v) => setPipelineFields({ ...pipelineFields, icp_type: v === "_none" ? "" : v })}>
                    <SelectTrigger className="border-gray-300 mt-1">
                      <SelectValue placeholder="Select ICP" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">Not Set</SelectItem>
                      <SelectItem value="ICP-1 International">ICP-1 International</SelectItem>
                      <SelectItem value="ICP-2 India SMB">ICP-2 India SMB</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Country</Label>
                  <Input className="border-gray-300 mt-1" placeholder="UAE / UK / US / India..." value={pipelineFields.country} onChange={(e) => setPipelineFields({ ...pipelineFields, country: e.target.value })} />
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Assigned To</Label>
                  <Select value={pipelineFields.assigned_to || "_unassigned"} onValueChange={(v) => setPipelineFields({ ...pipelineFields, assigned_to: v === "_unassigned" ? "" : v })}>
                    <SelectTrigger className="border-gray-300 mt-1">
                      <SelectValue placeholder="Assign to..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_unassigned">Unassigned</SelectItem>
                      <SelectItem value="Nayema">Nayema</SelectItem>
                      <SelectItem value="Divyanshu">Divyanshu</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Discovery Call Date</Label>
                  <Input className="border-gray-300 mt-1" placeholder="DD/MM/YYYY" value={pipelineFields.call_date} onChange={(e) => setPipelineFields({ ...pipelineFields, call_date: e.target.value })} />
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Client Budget</Label>
                  <Input className="border-gray-300 mt-1" placeholder="$5,000 / Rs 5L..." value={pipelineFields.budget} onChange={(e) => setPipelineFields({ ...pipelineFields, budget: e.target.value })} />
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Our Proposal Value</Label>
                  <Input className="border-gray-300 mt-1" placeholder="$5,000 / Rs 5L..." value={pipelineFields.proposal_value} onChange={(e) => setPipelineFields({ ...pipelineFields, proposal_value: e.target.value })} />
                </div>
                <div className="col-span-2">
                  <Label className="text-sm text-gray-500">Monthly Retainer (post-delivery)</Label>
                  <Input className="border-gray-300 mt-1" placeholder="$200/month or Rs 10,000/month..." value={pipelineFields.retainer_monthly} onChange={(e) => setPipelineFields({ ...pipelineFields, retainer_monthly: e.target.value })} />
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Expected Close Date</Label>
                  <Input type="date" className="border-gray-300 mt-1" value={pipelineFields.expected_close_date} onChange={(e) => setPipelineFields({ ...pipelineFields, expected_close_date: e.target.value })} />
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Invoice Amount</Label>
                  <Input className="border-gray-300 mt-1" placeholder="₹50,000 / $5,000..." value={pipelineFields.invoice_amount} onChange={(e) => setPipelineFields({ ...pipelineFields, invoice_amount: e.target.value })} />
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Invoice Raised Date</Label>
                  <Input type="date" className="border-gray-300 mt-1" value={pipelineFields.invoice_raised_date} onChange={(e) => setPipelineFields({ ...pipelineFields, invoice_raised_date: e.target.value })} />
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Invoice Due Date</Label>
                  <Input type="date" className="border-gray-300 mt-1" value={pipelineFields.invoice_due_date} onChange={(e) => setPipelineFields({ ...pipelineFields, invoice_due_date: e.target.value })} />
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Invoice Paid Date</Label>
                  <Input type="date" className="border-gray-300 mt-1" value={pipelineFields.invoice_paid_date} onChange={(e) => setPipelineFields({ ...pipelineFields, invoice_paid_date: e.target.value })} />
                </div>
                <div>
                  <Label className="text-sm text-gray-500">Invoice Status</Label>
                  <Select value={pipelineFields.invoice_status || "_none"} onValueChange={(v) => setPipelineFields({ ...pipelineFields, invoice_status: v === "_none" ? "" : v })}>
                    <SelectTrigger className="border-gray-300 mt-1"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_none">Not Set</SelectItem>
                      {['none','raised','overdue','paid','partial'].map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <Button onClick={savePipelineFields} className="mt-4 bg-indigo-600 hover:bg-indigo-700">
                <Save className="w-4 h-4 mr-2" />
                Save Pipeline Info
              </Button>
            </Card>

            {/* Trigger Signal — SUPREME only */}
            {isSupreme && (d.trigger_signal || editMode) && (
              <Card className={`p-6 bg-white border ${editMode ? 'border-amber-200' : 'border-amber-200'}`}>
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-amber-500" />
                  Trigger Signal
                </h2>
                {editMode ? (
                  <div className="space-y-3">
                    <EditableTextarea label="Signal" value={editData.trigger_signal?.signal} onChange={setTriggerField("signal")} rows={2} placeholder="What triggered this lead?" />
                    <div className="grid grid-cols-2 gap-3">
                      <EditableField label="Source" value={editData.trigger_signal?.source} onChange={setTriggerField("source")} />
                      <EditableField label="Date"   value={editData.trigger_signal?.date}   onChange={setTriggerField("date")} />
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {d.trigger_signal?.signal && (
                      <p className="text-sm text-gray-800 bg-amber-50 rounded-md p-3 border border-amber-100">
                        "{d.trigger_signal.signal}"
                      </p>
                    )}
                    <div className="flex gap-4 flex-wrap text-sm text-gray-500 mt-2">
                      {d.trigger_signal?.source && <span><span className="font-medium text-gray-700">Source:</span> {d.trigger_signal.source}</span>}
                      {d.trigger_signal?.date   && <span><span className="font-medium text-gray-700">Date:</span> {d.trigger_signal.date}</span>}
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Pain Point */}
            {(d.pain_point || editMode) && (
              <Card className={`p-6 bg-white border ${editMode ? 'border-amber-200' : 'border-gray-200'}`}>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Pain Point Analysis</h2>
                {editMode ? (
                  <EditableTextarea
                    value={editData.pain_point}
                    onChange={setField("pain_point")}
                    placeholder="Describe the pain point..."
                    rows={5}
                  />
                ) : (
                  <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{d.pain_point}</div>
                )}
              </Card>
            )}

            {/* Outreach Angle / Suggested Opener */}
            {(d.outreach_angle || editMode) && (
              <Card className={`p-6 bg-white border ${editMode ? 'border-amber-200' : 'border-gray-200'}`}>
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  {isSupreme ? "Suggested Opener" : "Outreach Strategy"}
                </h2>
                {editMode ? (
                  <EditableTextarea
                    value={editData.outreach_angle}
                    onChange={setField("outreach_angle")}
                    placeholder={isSupreme ? "Write a suggested opener message..." : "Describe the outreach strategy..."}
                    rows={4}
                  />
                ) : isSupreme ? (
                  <blockquote className="border-l-4 border-violet-300 pl-4 bg-violet-50 rounded-r-md p-4 text-sm text-gray-800 italic leading-relaxed">
                    "{d.outreach_angle}"
                  </blockquote>
                ) : (
                  <div className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{d.outreach_angle}</div>
                )}
              </Card>
            )}

            {/* Health Check — SUPREME only */}
            {isSupreme && (d.health_check || editMode) && (
              <Card className={`p-6 bg-white border ${editMode ? 'border-amber-200' : 'border-gray-200'}`}>
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-green-500" />
                  Health Check
                </h2>
                {editMode ? (
                  <div className="grid grid-cols-3 gap-4">
                    <EditableField label="Red Flags"   value={editData.health_check?.red_flags}   onChange={setHealthField("red_flags")} />
                    <EditableField label="Checked For" value={editData.health_check?.checked_for} onChange={setHealthField("checked_for")} />
                    <div>
                      <Label className="text-sm text-gray-500 font-normal mb-1 block">Confidence</Label>
                      <Select value={editData.health_check?.confidence || ""} onValueChange={setHealthField("confidence")}>
                        <SelectTrigger className="border-amber-300 bg-amber-50/40 h-9 text-sm">
                          <SelectValue placeholder="Select…" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="High">High</SelectItem>
                          <SelectItem value="Medium">Medium</SelectItem>
                          <SelectItem value="Low">Low</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500 mb-1">Red Flags</p>
                      <p className="text-gray-800">{d.health_check?.red_flags || "—"}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 mb-1">Checked For</p>
                      <p className="text-gray-800">{d.health_check?.checked_for || "—"}</p>
                    </div>
                    <div>
                      <p className="text-gray-500 mb-1">Confidence</p>
                      {d.health_check?.confidence ? (
                        <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${CONFIDENCE_COLORS[d.health_check.confidence] || 'text-gray-700 bg-gray-50'}`}>
                          {d.health_check.confidence}
                        </span>
                      ) : <p className="text-gray-800">—</p>}
                    </div>
                  </div>
                )}
              </Card>
            )}

            {/* Notes — always visible, separate from main edit flow */}
            <Card className="p-6 bg-white border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Notes</h2>
              <NoteThread leadId={id} notes={threadNotes} />
            </Card>
          </div>

          {/* ── Sidebar ──────────────────────────────────────────────────────── */}
          <div className="space-y-6">

            {/* Status Update */}
            <Card className="p-6 bg-white border border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Update Status</h2>
              <Select value={lead.status} onValueChange={updateStatus}>
                <SelectTrigger data-testid="status-select" className="border-gray-300">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="New">New</SelectItem>
                  <SelectItem value="Contacted">Contacted</SelectItem>
                  <SelectItem value="Follow-up">Follow-up</SelectItem>
                  <SelectItem value="Call Booked">Call Booked</SelectItem>
                  <SelectItem value="Proposal Sent">Proposal Sent</SelectItem>
                  <SelectItem value="Negotiating">Negotiating</SelectItem>
                  <SelectItem value="Won">Won</SelectItem>
                  <SelectItem value="Retainer">Retainer</SelectItem>
                  <SelectItem value="Lost">Lost</SelectItem>
                </SelectContent>
              </Select>
            </Card>

            {/* Outreach Timeline */}
            <Card className="p-6 bg-white border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-900">Outreach History</h2>
                <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                  <DialogTrigger asChild>
                    <Button data-testid="add-outreach-button" size="sm" variant="outline" className="border-gray-300">
                      <Plus className="w-4 h-4" />
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="bg-white">
                    <DialogHeader>
                      <DialogTitle>Create New Outreach</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label>Type</Label>
                        <Select value={newOutreach.type} onValueChange={(val) => setNewOutreach({ ...newOutreach, type: val })}>
                          <SelectTrigger data-testid="outreach-type-select" className="border-gray-300">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="LinkedIn">LinkedIn</SelectItem>
                            <SelectItem value="Email">Email</SelectItem>
                            <SelectItem value="Phone">Phone</SelectItem>
                            <SelectItem value="Other">Other</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Status</Label>
                        <Select value={newOutreach.status} onValueChange={(val) => setNewOutreach({ ...newOutreach, status: val })}>
                          <SelectTrigger data-testid="outreach-status-select" className="border-gray-300">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Drafted">Drafted</SelectItem>
                            <SelectItem value="Sent">Sent</SelectItem>
                            <SelectItem value="Replied">Replied</SelectItem>
                            <SelectItem value="No Response">No Response</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Content</Label>
                        {isSupreme && lead.outreach_angle && newOutreach.content === '' && (
                          <p className="text-xs text-violet-600 mb-1 cursor-pointer hover:underline"
                             onClick={() => setNewOutreach({ ...newOutreach, content: lead.outreach_angle })}>
                            ↑ Use suggested opener
                          </p>
                        )}
                        <Textarea
                          data-testid="outreach-content-textarea"
                          placeholder="Enter outreach message..."
                          value={newOutreach.content}
                          onChange={(e) => setNewOutreach({ ...newOutreach, content: e.target.value })}
                          className="min-h-32 border-gray-300"
                        />
                      </div>
                      <Button
                        data-testid="create-outreach-button"
                        onClick={createOutreach}
                        className="w-full bg-gray-900 hover:bg-gray-800"
                      >
                        Create Outreach
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>

              <div className="space-y-4">
                {outreach.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-8">No outreach recorded yet</p>
                ) : (
                  outreach.map((item) => (
                    <div key={item.id} data-testid={`outreach-item-${item.id}`} className="border-l-2 border-gray-200 pl-4 pb-4">
                      <div className="flex items-center justify-between mb-2">
                        <Badge className={`${OUTREACH_STATUS_COLORS[item.status]} border text-xs`}>
                          {item.status}
                        </Badge>
                        <span className="text-xs text-gray-500">{item.created_date}</span>
                      </div>
                      <p className="text-sm font-medium text-gray-900 mb-1">{item.outreach_type}</p>
                      {item.content && (
                        <p className="text-sm text-gray-600 line-clamp-3">{item.content}</p>
                      )}
                      <div className="mt-2 flex gap-2">
                        {item.status !== "Sent" && item.status !== "Replied" && (
                          <Button size="sm" variant="outline" className="border-gray-300 text-xs" onClick={() => updateOutreachStatus(item.id, "Sent")}>
                            <Send className="w-3 h-3 mr-1" />Mark Sent
                          </Button>
                        )}
                        {item.status === "Sent" && (
                          <Button size="sm" variant="outline" className="border-gray-300 text-xs" onClick={() => updateOutreachStatus(item.id, "Replied")}>
                            <MessageCircle className="w-3 h-3 mr-1" />Mark Replied
                          </Button>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </Card>
          </div>
        </div>
      </div>

      {/* ── Create Outreach Sheet ─────────────────────────────────────────── */}
      <Sheet open={dialogOpen} onOpenChange={setDialogOpen}>
        <SheetContent side="right" className="w-[75vw] sm:max-w-[75vw] overflow-y-auto">
          <SheetHeader className="mb-6">
            <SheetTitle>Create New Outreach</SheetTitle>
          </SheetHeader>
          <div className="space-y-4">
            <div>
              <Label>Type</Label>
              <Select value={newOutreach.type} onValueChange={(val) => setNewOutreach({ ...newOutreach, type: val })}>
                <SelectTrigger data-testid="outreach-type-select" className="border-gray-300 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="LinkedIn">LinkedIn</SelectItem>
                  <SelectItem value="Email">Email</SelectItem>
                  <SelectItem value="Phone">Phone</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={newOutreach.status} onValueChange={(val) => setNewOutreach({ ...newOutreach, status: val })}>
                <SelectTrigger data-testid="outreach-status-select" className="border-gray-300 mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Drafted">Drafted</SelectItem>
                  <SelectItem value="Sent">Sent</SelectItem>
                  <SelectItem value="Replied">Replied</SelectItem>
                  <SelectItem value="No Response">No Response</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Content</Label>
              {isSupreme && lead?.outreach_angle && newOutreach.content === '' && (
                <p className="text-xs text-violet-600 mb-1 cursor-pointer hover:underline"
                   onClick={() => setNewOutreach({ ...newOutreach, content: lead.outreach_angle })}>
                  Use suggested opener
                </p>
              )}
              <Textarea
                data-testid="outreach-content-textarea"
                placeholder="Enter outreach message..."
                value={newOutreach.content}
                onChange={(e) => setNewOutreach({ ...newOutreach, content: e.target.value })}
                className="min-h-40 border-gray-300 mt-1"
              />
            </div>
            <Button
              data-testid="create-outreach-button"
              onClick={createOutreach}
              className="w-full bg-gray-900 hover:bg-gray-800"
            >
              Create Outreach
            </Button>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default LeadDetail;